import { useEffect, useMemo, useState } from "react";
import {
  Star,
  Zap,
  CalendarDays,
  Trophy,
  RefreshCw,
  CheckCircle2,
  Crown,
} from "lucide-react";
import { toast } from "sonner";
import { authClient, formatApiError } from "@/lib/auth";

const PERIODS = [
  {
    value: "day",
    label: "Build of the Day",
    icon: CalendarDays,
  },
  {
    value: "week",
    label: "Build of the Week",
    icon: Trophy,
  },
  {
    value: "month",
    label: "Build of the Month",
    icon: Star,
  },
];

const EMPTY = {
  day: null,
  week: null,
  month: null,
};

export default function AdminBuildFeatured() {
  const [period, setPeriod] = useState("day");
  const [buildId, setBuildId] = useState("");
  const [priority, setPriority] = useState(0);
  const [reason, setReason] = useState("");
  const [server, setServer] = useState("");

  const [builds, setBuilds] = useState([]);
  const [featured, setFeatured] = useState(EMPTY);

  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);

    try {
      const [buildRes, featuredRes] = await Promise.all([
        authClient.get("/builds"),
        authClient.get("/builds/featured/current"),
      ]);

      setBuilds(Array.isArray(buildRes.data) ? buildRes.data : []);
      setFeatured(featuredRes.data || EMPTY);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approvedBuilds = useMemo(
    () =>
      builds.filter(
        (b) => b && b.approved === true
      ),
    [builds]
  );

  const servers = useMemo(() => {
    return [
      ...new Set(
        approvedBuilds
          .map((b) => b.server)
          .filter(Boolean)
      ),
    ];
  }, [approvedBuilds]);

  const filteredBuilds = useMemo(() => {
    if (!server) return approvedBuilds;

    return approvedBuilds.filter(
      (b) => b.server === server
    );
  }, [approvedBuilds, server]);

  const current = featured?.[period];

  const manualSelect = async () => {
    if (!buildId) {
      toast.error("Select a build first.");
      return;
    }

    setLoading(true);

    try {
      await authClient.post("/builds/featured/manual", {
        period,
        build_id: buildId,
        priority: Number(priority) || 0,
        reason:
          reason.trim() ||
          `Manually selected as ${period} featured build.`,
        server: server || undefined,
      });

      toast.success(
        `Build of the ${period} selected successfully.`
      );

      setReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const autoSelect = async () => {
    setAutoLoading(true);

    try {
      await authClient.post("/builds/featured/auto", {
        period,
        server: server || undefined,
        priority: Number(priority) || 0,
      });

      toast.success(
        `Automatic ${period} featured build selected.`
      );

      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <section className="mt-6 space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl border border-[#FFBF00]/20 bg-black/50 p-6 sm:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,191,0,0.12),transparent_45%)]" />

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div>
            <div className="flex items-center gap-2 text-[#FFBF00] text-[10px] uppercase tracking-[0.3em] font-bold">
              <Crown size={14} />
              LostDC Showcase
            </div>

            <h2 className="font-hero text-2xl sm:text-3xl text-white mt-2">
              Featured Builds
            </h2>

            <p className="text-sm text-white/45 mt-2 max-w-2xl">
              Manually select or automatically rank approved community
              builds for the Day, Week and Month showcase.
            </p>
          </div>

          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white text-xs uppercase tracking-widest transition-all disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={refreshing ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Current Featured */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PERIODS.map((item) => {
          const Icon = item.icon;
          const value = featured?.[item.value];

          return (
            <div
              key={item.value}
              className={`relative overflow-hidden rounded-2xl border ${
                value
                  ? "border-[#FFBF00]/30"
                  : "border-white/10"
              } bg-black/50 p-5`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#FFBF00]">
                  <Icon size={16} />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold">
                    {item.label}
                  </span>
                </div>

                {value && (
                  <CheckCircle2
                    size={16}
                    className="text-green-400"
                  />
                )}
              </div>

              {value ? (
                <div className="mt-4">
                  {value.images?.[0] || value.image ? (
                    <img
                      src={
                        value.images?.[0] ||
                        value.image
                      }
                      alt={value.title}
                      className="w-full h-32 object-cover rounded-xl"
                    />
                  ) : null}

                  <div className="text-white font-semibold mt-3 truncate">
                    {value.title}
                  </div>

                  <div className="text-xs text-white/45 mt-1">
                    by{" "}
                    {value.author_name ||
                      value.author ||
                      "Unknown"}
                  </div>

                  <div className="text-[9px] uppercase tracking-widest text-[#FFBF00] mt-3">
                    {value.featured?.selection_mode === "auto"
                      ? "Automatically Selected"
                      : "Manually Selected"}
                  </div>
                </div>
              ) : (
                <div className="mt-8 text-center text-white/25 text-sm">
                  No featured build
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div className="rounded-3xl border border-white/10 bg-black/40 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Period */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-2">
              Featured Period
            </label>

            <div className="grid grid-cols-3 gap-2">
              {PERIODS.map((item) => {
                const Icon = item.icon;

                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() =>
                      setPeriod(item.value)
                    }
                    className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-[10px] uppercase tracking-widest transition-all ${
                      period === item.value
                        ? "bg-[#FFBF00] text-black font-bold"
                        : "bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-[#FFBF00]/40"
                    }`}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">
                      {item.value}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Server */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-2">
              Server
            </label>

            <select
              value={server}
              onChange={(e) => {
                setServer(e.target.value);
                setBuildId("");
              }}
              className="w-full rounded-xl bg-black border border-white/10 px-4 py-3 text-sm text-white outline-none focus:border-[#FFBF00]/50"
            >
              <option value="">
                All Servers
              </option>

              {servers.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          {/* Build */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-2">
              Build
            </label>

            <select
              value={buildId}
              onChange={(e) =>
                setBuildId(e.target.value)
              }
              className="w-full rounded-xl bg-black border border-white/10 px-4 py-3 text-sm text-white outline-none focus:border-[#FFBF00]/50"
            >
              <option value="">
                Select approved build
              </option>

              {filteredBuilds.map((build) => (
                <option
                  key={build.id}
                  value={build.id}
                >
                  {build.title} —{" "}
                  {build.server || "No Server"}
                </option>
              ))}
            </select>

            <div className="text-[10px] text-white/30 mt-2">
              {filteredBuilds.length} approved build
              {filteredBuilds.length === 1 ? "" : "s"} available
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-2">
              Priority
            </label>

            <input
              type="number"
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value)
              }
              className="w-full rounded-xl bg-black border border-white/10 px-4 py-3 text-sm text-white outline-none focus:border-[#FFBF00]/50"
              placeholder="0"
            />

            <div className="text-[10px] text-white/30 mt-2">
              Higher priority wins during automatic selection.
            </div>
          </div>

          {/* Reason */}
          <div className="lg:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-2">
              Selection Reason
            </label>

            <textarea
              value={reason}
              onChange={(e) =>
                setReason(e.target.value)
              }
              rows={3}
              className="w-full rounded-xl bg-black border border-white/10 px-4 py-3 text-sm text-white outline-none resize-none focus:border-[#FFBF00]/50"
              placeholder="Why is this build being featured?"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <button
            type="button"
            onClick={manualSelect}
            disabled={loading || !buildId}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? (
              <RefreshCw
                size={15}
                className="animate-spin"
              />
            ) : (
              <Star size={15} />
            )}
            Feature Selected Build
          </button>

          <button
            type="button"
            onClick={autoSelect}
            disabled={autoLoading}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-[#FFBF00]/30 bg-[#FFBF00]/10 hover:bg-[#FFBF00]/20 text-[#FFBF00] text-xs uppercase tracking-widest font-bold transition-all disabled:opacity-40"
          >
            {autoLoading ? (
              <RefreshCw
                size={15}
                className="animate-spin"
              />
            ) : (
              <Zap size={15} />
            )}
            Auto Select Best Build
          </button>
        </div>

        <div className="mt-5 rounded-xl border border-white/5 bg-white/[0.02] p-4">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#FFBF00] font-bold mb-2">
            Automatic Ranking
          </div>

          <div className="text-xs text-white/45 leading-relaxed">
            Priority → Likes → Favorites → Views → Newest build
          </div>
        </div>
      </div>
    </section>
  );
}
